import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Typography, makeStyles } from '@material-ui/core';
import AIAssistantStateIcon from './AIAssistantStateIcon';

// Full-screen shield while the board is being tidied: nothing underneath can be touched (the
// ink snapshot was already taken, so a stroke drawn now would be lost), and a scanning band
// plus the assistant's animated face show that work is in progress.
const useStyles = makeStyles((theme) => ({
    '@keyframes tidyScan': {
        from: { transform: 'translateY(-100%)' },
        to: { transform: 'translateY(100vh)' },
    },
    '@keyframes tidyPulse': {
        '0%': { opacity: 0.55 },
        '50%': { opacity: 1 },
        '100%': { opacity: 0.55 },
    },
    root: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: theme.zIndex.snackbar - 2,
        backgroundColor: 'rgba(255, 255, 255, 0.72)',
        overflow: 'hidden',
        cursor: 'progress',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none',
    },
    scan: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        height: 160,
        background: `linear-gradient(180deg, rgba(255,255,255,0) 0%, ${theme.palette.primary.main} 50%, rgba(255,255,255,0) 100%)`,
        opacity: 0.22,
        animation: '$tidyScan 2.2s linear infinite',
        pointerEvents: 'none',
    },
    card: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(3, 4),
        borderRadius: 16,
        backgroundColor: '#fff',
        boxShadow: theme.shadows[8],
        minWidth: 260,
    },
    face: {
        marginBottom: theme.spacing(1.5),
    },
    label: {
        fontSize: '1rem',
        fontWeight: 600,
        animation: '$tidyPulse 1.6s ease-in-out infinite',
    },
    sub: {
        marginTop: theme.spacing(0.5),
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
    },
}));

const stop = (event) => {
    event.preventDefault();
    event.stopPropagation();
};

const TidyOverlay = ({ elapsedSeconds = 0 }) => {
    const classes = useStyles();
    return (
        <div
            className={classes.root}
            role='alert'
            aria-busy='true'
            onPointerDown={stop}
            onMouseDown={stop}
            onTouchStart={stop}
            onWheel={stop}
            onContextMenu={stop}
        >
            <div className={classes.scan} />
            <div className={classes.card}>
                <AIAssistantStateIcon state='tidying' size={128} className={classes.face} />
                <Typography className={classes.label}>
                    <FormattedMessage id='ai.tidy.working' />
                </Typography>
                <Typography className={classes.sub}>
                    <FormattedMessage id='ai.tidy.workingHint' />
                </Typography>
                {elapsedSeconds > 0 ? (
                    <Typography className={classes.sub}>
                        <FormattedMessage id='ai.tidy.elapsed' values={{ seconds: elapsedSeconds }} />
                    </Typography>
                ) : null}
            </div>
        </div>
    );
};

export default TidyOverlay;
